/**
 * Tiny line glyphs for the parts rail, one per part.
 *
 * The same idea as the chemistry bench's apparatus tiles, and drawn for the
 * same reason: there is no emoji for "ceiling hook" or "sheave", and the
 * nearest stand-ins say the wrong thing. Each glyph is the silhouette the
 * bench itself draws — a hook hanging under a beam, a wheel with a groove and
 * a hub, a square weight on a hanger —
 * so a tile can never disagree with what appears when you press it.
 *
 * A 24-box, 1.6 stroke, `currentColor`, no fills: the tile's own colour then
 * carries hover and disabled without the glyph knowing about either.
 */
/**
 * What the glyphs are of, which is not the same list as the engine's part
 * kinds: these are pictures, and a picture of a pulley is a pulley whatever
 * the code calls the wheel. The bench maps one to the other.
 */
export type PartGlyph = 'anchor' | 'pulley' | 'block' | 'table' | 'ramp' | 'gauge' | 'connector';

const GLYPH: Record<PartGlyph, React.JSX.Element> = {
  // A beam with a bracket bolted under it and an eye hanging from that.
  anchor: (
    <>
      <line x1="3" y1="6" x2="21" y2="6" />
      <rect x="9" y="6" width="6" height="2.5" rx="1" />
      <line x1="12" y1="8.5" x2="12" y2="13" />
      <circle cx="12" cy="15" r="2.2" />
    </>
  ),
  // A sheave: rim, rope groove, hub — and the rope coming down both sides.
  pulley: (
    <>
      <circle cx="12" cy="11" r="6" />
      <circle cx="12" cy="11" r="3.4" />
      <circle cx="12" cy="11" r="0.9" />
      <line x1="6" y1="11" x2="6" y2="20" />
      <line x1="18" y1="11" x2="18" y2="20" />
    </>
  ),
  // A weight on its hanger.
  block: (
    <>
      <line x1="12" y1="3" x2="12" y2="8" />
      <rect x="6" y="8" width="12" height="11" rx="1.5" />
    </>
  ),
  // A block standing on a flat top, with the hatching the bench draws.
  table: (
    <>
      <rect x="7" y="6" width="10" height="7" rx="1" />
      <line x1="2" y1="14" x2="22" y2="14" />
      <line x1="4" y1="14" x2="2" y2="18" />
      <line x1="9" y1="14" x2="7" y2="18" />
      <line x1="14" y1="14" x2="12" y2="18" />
      <line x1="19" y1="14" x2="17" y2="18" />
    </>
  ),
  // The same block, on a slope.
  ramp: (
    <>
      <line x1="3" y1="17" x2="21" y2="7" />
      <line x1="5" y1="19" x2="3" y2="21" />
      <line x1="11" y1="16" x2="9" y2="20" />
      <line x1="17" y1="12" x2="15" y2="17" />
      <rect x="10" y="6" width="7" height="5" rx="1" transform="rotate(-29 13.5 8.5)" />
    </>
  ),
  // A dial on a line, with a needle.
  gauge: (
    <>
      <line x1="12" y1="2" x2="12" y2="6" />
      <rect x="6" y="6" width="12" height="12" rx="2" />
      <path d="M8.5 14a3.6 3.6 0 0 1 7 0" />
      <line x1="12" y1="14" x2="14.6" y2="11.6" />
      <line x1="12" y1="18" x2="12" y2="22" />
    </>
  ),
  // The two lines a join can be: a rope with a little give in it, and a rod,
  // straight and pinned at both ends.
  connector: (
    <>
      <path d="M3 19C6 15 8 9 11 4" />
      <line x1="14.5" y1="19.5" x2="20.5" y2="4.5" />
      <circle cx="14.5" cy="19.5" r="1.3" />
      <circle cx="20.5" cy="4.5" r="1.3" />
    </>
  ),
};

export function PartIcon({ kind }: { kind: PartGlyph }): React.JSX.Element {
  return (
    <svg
      className="mbench-icon"
      viewBox="0 0 24 24"
      width="26"
      height="26"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {GLYPH[kind]}
    </svg>
  );
}
